import { normalizeAnswers } from "../../../pearliCore/assessment";
import React, { useState, useEffect, useCallback, useRef } from "react";
import "./Card.css";
import ArrowCircleRight from "../../../assets/ArrowCircleRight.svg";
import api from "../../../axiosconfig.js";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { selectAssessmentId } from "../../../store/authSlice.js";
import { toast } from "react-toastify";

export const Card = ({
  domain,
  completedTabs,
  activeTab: initialActiveTab,
  onComplete,
  onDomainProgressUpdate,
}) => {
  const [progress, setProgress] = useState(0);
  const [answers, setAnswers] = useState(Array(5).fill(undefined));
  const [isSaving, setIsSaving] = useState(false);
  const [isLoadingQuestions, setIsLoadingQuestions] = useState(false);
  const [activeTab, setActiveTab] = useState(domain);
  const [currentQuestion, setCurrentQuestion] = useState("");
  const [questionIndex, setQuestionIndex] = useState(0);
  const [questions, setQuestions] = useState([]); // Array of question objects with code, text, polarity
  const [isAnswerSelected, setIsAnswerSelected] = useState(false);
  const [currentAnswer, setCurrentAnswer] = useState(undefined);
  const [domainQuestionCount, setDomainQuestionCount] = useState(0);

  const reduxAssessmentId = useSelector(selectAssessmentId);
  const getActiveAssessmentId = useCallback(() => {
    return reduxAssessmentId || (typeof window !== "undefined" ? localStorage.getItem("assessmentId") : null);
  }, [reduxAssessmentId]);

  const lastFetchedDomainRef = useRef("");
  const isFetchingQuestionsRef = useRef(false);
  const onDomainProgressUpdateRef = useRef(onDomainProgressUpdate);
  onDomainProgressUpdateRef.current = onDomainProgressUpdate;

  const API_URL = process.env.REACT_APP_API_URL;
  const navigate = useNavigate();

  const normalizeDomain = (d) => (d || "physical").toLowerCase().replace(/[_-]/g, " ");

  // Sync activeTab when domain prop changes
  useEffect(() => {
    if (domain) {
      setActiveTab(domain);
    }
  }, [domain]);

  // Capitalize domain name for API request
  const domainFormatted =
    activeTab.charAt(0).toUpperCase() + activeTab.slice(1);

  // Check for assessmentId at component mount - redirect if missing
  useEffect(() => {
    const assessmentId = getActiveAssessmentId();
    if (!assessmentId) {
      toast.error("Assessment ID not found. Please start a new assessment.");
      navigate("/Disclaimer");
    }
  }, [navigate, getActiveAssessmentId]); // Run once on mount

  /**
   * Fetch questions from the backend for this domain using the new endpoint
   * GET /api/questions/domain?domain=<domain>&assessmentId=<assessmentId>
   * 
   * localStorage stores assessmentId to avoid re-creating assessments per session.
   * Questions are fetched once per domain change and stored in state.
   */
  const fetchQuestions = useCallback(async (targetDomain) => {
    const rawDomain = targetDomain || "physical";
    const normalizedDomain = rawDomain.toLowerCase().replace(/[\s-]+/g, "_");

    if (!normalizedDomain) return;
    if (lastFetchedDomainRef.current === normalizedDomain && isFetchingQuestionsRef.current) {
      return;
    }

    try {
      isFetchingQuestionsRef.current = true;
      setIsLoadingQuestions(true);

      // Retrieve assessment ID from Redux / safe storage
      const assessmentId = getActiveAssessmentId();
      if (!assessmentId) {
        toast.error("Assessment ID not found. Please start a new assessment.");
        navigate("/Disclaimer");
        return;
      }

      // Call backend endpoint to fetch questions for this domain
      const response = await api.get(`${API_URL}/api/pearli/questions/domain`, {
        params: {
          domain: normalizedDomain,
          assessmentId: assessmentId,
        },
      });

      if (response.status === 200 && response.data?.questions) {
        lastFetchedDomainRef.current = normalizedDomain;
        setQuestions(response.data.questions);
        setDomainQuestionCount(response.data.questions.length);
        
        // Reset answers for this domain (prevents data mutation across domains)
        setAnswers(Array(5).fill(undefined));
        setQuestionIndex(0);
        setProgress(0);
        setCurrentAnswer(undefined);
        setIsAnswerSelected(false);

        onDomainProgressUpdateRef.current?.(rawDomain, 0);
      } else {
        toast.error("Failed to load questions for this domain.");
        setCurrentQuestion("Failed to load questions.");
      }
    } catch (error) {
      console.error("Error fetching questions:", error);
      
      if (error.response?.status === 400) {
        toast.error(error.response.data?.error || "Invalid domain or assessment.");
      } else if (error.response?.status === 404) {
        toast.error("Questions not found for this domain.");
      } else {
        toast.error("Error loading questions. Please try again.");
      }
      
      setCurrentQuestion("Error loading question.");
    } finally {
      setIsLoadingQuestions(false);
      isFetchingQuestionsRef.current = false;
    }
  }, [API_URL, navigate, getActiveAssessmentId]);

  /**
   * Handle clicking the reflection button to move to the next question
   * or submit all answers if at the 5th (last) question.
   */
  const handleStartReflection = async () => {
    if (isSaving) return;

    let answerToSave = currentAnswer;
    if (answerToSave === undefined && answers[questionIndex] !== undefined) {
      answerToSave = answers[questionIndex];
    }

    if (answerToSave === undefined) {
      toast.warn("Please select an answer before proceeding.");
      return;
    }

    // Keep raw choices in state; normalize once at submission.
    const updatedAnswers = [...answers];
    updatedAnswers[questionIndex] = answerToSave;
    setAnswers(updatedAnswers);

    // Update progress metrics
    const answeredQuestions = updatedAnswers.filter(
      (answer) => answer !== undefined
    ).length;

    if (onDomainProgressUpdate) {
      const progressValue = (answeredQuestions / (domainQuestionCount || 5)) * 100;
      onDomainProgressUpdate(activeTab, progressValue);
    }

    // Move to next question or submit domain
    if (questionIndex < questions.length - 1) {
      setQuestionIndex(questionIndex + 1);
      setProgress(
        updatedAnswers[questionIndex + 1] !== undefined
          ? updatedAnswers[questionIndex + 1]
          : 0
      );
      setCurrentAnswer(updatedAnswers[questionIndex + 1]);
      setIsAnswerSelected(updatedAnswers[questionIndex + 1] !== undefined);
    } else {
      // 5th (last) question - submit all answers
      const validAnswerCount = updatedAnswers.filter((answer) => answer !== undefined).length;
      if (validAnswerCount === 5) {
        const isSaved = await saveScores(updatedAnswers);
        if (isSaved) {
          if (onComplete) {
            onComplete(domainFormatted);
          }

          const tabFlow = [
            "physical",
            "emotional",
            "acceptance",
            "relationships",
            "loving kindness",
            "intellectual",
          ];
          const currentNormalized = normalizeDomain(activeTab || domain);
          const currentIdx = tabFlow.indexOf(currentNormalized);
          const resolvedNextDomain = currentIdx >= 0 && currentIdx < tabFlow.length - 1 ? tabFlow[currentIdx + 1] : null;

          if (resolvedNextDomain) {
            navigate(`/Assessment/maincontent/${resolvedNextDomain}`);
          } else {
            navigate("/PEARLIReports");
          }
        }
      } else {
        toast.error("Please answer all 5 questions in this section.");
      }
    }
  };

  /**
   * Handle clicking on the progress bar to set score manually
   * Converts click position to a score between 1-5
   */
  const handleProgressBarClick = (e) => {
    const barWidth = e.currentTarget.offsetWidth;
    const clickPosition =
      e.clientX - e.currentTarget.getBoundingClientRect().left;
    const rawProgress = (clickPosition / barWidth) * 5;
    const newProgress =
      Math.max(1, Math.min(5, Math.ceil(rawProgress)));

    if (!isSaving) {
      setProgress(newProgress);
      setCurrentAnswer(newProgress);
      setIsAnswerSelected(true);
      const updatedAnswers = [...answers];
      updatedAnswers[questionIndex] = newProgress;
      setAnswers(updatedAnswers);
    }
  };

  /**
   * Handle direct answer selection from buttons
   */
  const handleAnswerSelect = (selectedScore) => {
    if (!isSaving) {
      setProgress(selectedScore);
      setCurrentAnswer(selectedScore);
      setIsAnswerSelected(true);
      const updatedAnswers = [...answers];
      updatedAnswers[questionIndex] = selectedScore;
      setAnswers(updatedAnswers);
    }
  };

  // Toast notifications for save success/failure
  const notifySuccess = () => toast.success("Scores saved successfully!");
  const notifyError = () => toast.error("Submission failed. Please try again.");

  /**
   * Save user scores to backend using PATCH endpoint
   * PATCH /api/assessments/{assessmentId}/domains/{domain}
   * Body: { answers: [1-5, ...], questions: [code, ...] }
   * 
   * Answers are already processed with polarity applied.
   * Questions array contains the question codes.
   */
  const saveScores = async (scores) => {
    if (scores.length === 5 && scores.every((s) => s !== undefined)) {
      try {
        setIsSaving(true);

        // Retrieve assessment ID from Redux / safe storage
        const assessmentId = getActiveAssessmentId();
        if (!assessmentId) {
          toast.error("Assessment ID not found. Please start a new assessment.");
          navigate("/Disclaimer");
          return false;
        }

        // Extract question codes for the request
        const questionCodes = questions.map((q) => q.code);

        // Normalize domain: replace spaces with underscores for backend compatibility
        const normalizedDomain = (activeTab || domain || "").toLowerCase().replace(/[\s-]+/g, "_");

        // Build request body according to backend contract
        const dataToSend = {
          scores: normalizeAnswers(scores, questions.map(q => q.polarity)),
          rawScores: scores,
          questions: questionCodes,
        };

        // Send PATCH request to update domain scores
        const response = await api.patch(
          `${API_URL}/api/assessments/${assessmentId}/domains/${normalizedDomain}`,
          dataToSend
        );

        if (response.status === 200) {
          notifySuccess();
          // Reset answers for next domain to prevent data mutation
          setAnswers(Array(5).fill(undefined));
          return true;
        } else {
          toast.error("Error saving scores");
          return false;
        }
      } catch (error) {
        console.error("Error saving scores:", error);
        
        if (error.response?.status === 400) {
          toast.error(error.response.data?.error || "Invalid domain or answers.");
        } else if (error.response?.status === 404) {
          toast.error("Assessment not found.");
        } else {
          notifyError();
        }
        return false;
      } finally {
        setIsSaving(false);
      }
    }
    return false;
  };

  // Fetch questions when component mounts or active domain changes
  const currentDomainNormalized = (activeTab || domain || "physical").toLowerCase().replace(/[\s-]+/g, "_");

  useEffect(() => {
    if (lastFetchedDomainRef.current !== currentDomainNormalized) {
      fetchQuestions(currentDomainNormalized);
    }
  }, [currentDomainNormalized, fetchQuestions]);

  // Store current question index in localStorage (optional feature)
  useEffect(() => {
    if (questionIndex >= 0) {
      localStorage.setItem("lastQuestionIndex", questionIndex);
    }
  }, [questionIndex]);

  // Update question and progress on question index change
  useEffect(() => {
    if (questions.length > 0) {
      setCurrentQuestion(questions[questionIndex]?.text || "");
      setProgress(
        answers[questionIndex] !== undefined ? answers[questionIndex] : 0
      );
      setCurrentAnswer(answers[questionIndex]);
      setIsAnswerSelected(answers[questionIndex] !== undefined);
    } else {
      setCurrentQuestion("No questions available.");
    }
  }, [questionIndex, questions, answers]);

  // Handle "Revisit" button to go back to previous question
  const handleRevert = () => {
    if (questionIndex > 0) {
      const previousIndex = questionIndex - 1;
      setQuestionIndex(previousIndex);
    }
  };

  // Show loading state while fetching questions
  if (isLoadingQuestions) {
    return (
      <div className="card-tab">
        <div className="Card">
          <p className="text-card">Loading questions... 🌱</p>
        </div>
      </div>
    );
  }

  return (
    <div className="card-tab">
      <div className="Card">
        <p className="text-card">Reflecting on Your Well-Being 🌱</p>
        <div className="emoji-progrss">
          <div className="emoji-progress-defult">
            <div className="container-emoji">
              <div className="statement-container">
                <div className="frame-70">
                  <span className="text-name">
                    {`Question ${Math.min(
                      questionIndex + 1,
                      questions.length
                    )} of ${questions.length}`}
                  </span>
                  <div className="bedge-emoji">
                    <span>How accurate is this statement?</span>
                  </div>
                </div>
                <p className="emoji-text">{currentQuestion}</p>
              </div>

              <div className="emoji-progrss-bar">
                <div className="bar-container">
                  <div className="bar" onClick={handleProgressBarClick}>
                    <div className="vector1">
                      <div className="bar-background" />
                      <div
                        className="bar-fill"
                        style={{ width: `${(progress / 5) * 100}%` }}
                      />
                      {/* Add vertical lines */}
                      {[1, 2, 3, 4].map((i) => (
                        <div
                          key={i}
                          className="vertical-line"
                          style={{ left: `${(i / 5) * 100}%` }}
                          onClick={() => handleAnswerSelect(i + 1)}
                          disabled={isSaving}
                        />
                      ))}
                      {/* <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="400"
                      height="16"
                      viewBox="0 0 400 16"
                      fill="none"
                    >
                      <path
                        d="M392.687 16H2.34428C1.04957 16 0 14.8519 0 13.4355C0 12.0447 1.01354 10.9073 2.2846 10.8719L392.501 0.00264745C396.611 -0.111848 400 3.50202 400 8.00003C400 12.4183 396.726 16 392.687 16Z"
                        fill="#E2E8F0"
                        strokeLinejoin="round"
                      />
                      {[0, 80, 160, 240, 320].map((x, i) => (
                        <line
                          key={i}
                          x1={x}
                          y1="2"
                          x2={x}
                          y2="14"
                          stroke={i === 0 ? "#0C8F63" : "#475569"}
                        />
                      ))}
                    </svg> */}
                    </div>
                  </div>

                  <div className="frame-75">
                    {[
                      "Totally Disagree",
                      "Disagree",
                      "Neutral",
                      "Agree",
                      "Totally Agree",
                    ].map((text, i) => (
                      <p
                        key={i}
                        className={`text-frame75 ${
                          progress === i + 1 ? "selected-text" : ""
                        }`}
                        onClick={() => handleAnswerSelect(i + 1)}
                        disabled={isSaving}
                      >
                        {text}
                      </p>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="alert-emoji">
            <div className="stack-emoji">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
              >
                <path
                  d="M9.99984 18.3337C14.6022 18.3337 18.3332 14.6027 18.3332 10.0003C18.3332 5.39795 14.6022 1.66699 9.99984 1.66699C5.39746 1.66699 1.6665 5.39795 1.6665 10.0003C1.6665 14.6027 5.39746 18.3337 9.99984 18.3337Z"
                  stroke="#1F2937"
                  stroke-width="1.66667"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
                <path
                  d="M10 13.3333V10"
                  stroke="#1F2937"
                  stroke-width="1.66667"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
                <path
                  d="M10 6.66699H10.0083"
                  stroke="#1F2937"
                  stroke-width="1.66667"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
              <div className="Stack-emoji-text">
                <p className="stack-text">
                  Research shows honest self-reflection boosts well-being.
                </p>
              </div>
            </div>
          </div>
        </div>
        <div className="card-button">
          <div className="card-btn-group">
            <button className="card-btn1" onClick={handleRevert}>
              <p className="btn1-text ">Revisit</p>
              <div className="revist-icon">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="14"
                  height="12"
                  viewBox="0 0 14 12"
                  fill="none"
                >
                  <path
                    d="M13.5003 1.49944V4.49944C13.5003 4.63205 13.4477 4.75922 13.3539 4.85299C13.2601 4.94676 13.1329 4.99944 13.0003 4.99944H10.0003C9.90138 4.99952 9.80464 4.97023 9.72234 4.9153C9.64004 4.86036 9.5759 4.78224 9.53802 4.69083C9.50014 4.59942 9.49024 4.49883 9.50957 4.40178C9.52889 4.30474 9.57657 4.21562 9.64658 4.14569L10.7091 3.08319L10.0497 2.48006L10.0341 2.46506C9.33912 1.77037 8.45488 1.29585 7.4918 1.10078C6.52873 0.90571 5.52952 0.998741 4.61901 1.36825C3.7085 1.73776 2.92706 2.36736 2.37229 3.17841C1.81753 3.98946 1.51404 4.946 1.49975 5.92853C1.48545 6.91105 1.76098 7.87601 2.29191 8.70286C2.82285 9.5297 3.58565 10.1818 4.48502 10.5776C5.3844 10.9735 6.38047 11.0955 7.34881 10.9286C8.31715 10.7616 9.21483 10.313 9.9297 9.63881C10.0261 9.54764 10.1548 9.4985 10.2874 9.50219C10.42 9.50588 10.5457 9.56211 10.6369 9.6585C10.7281 9.75489 10.7772 9.88355 10.7735 10.0162C10.7698 10.1488 10.7136 10.2745 10.6172 10.3657C9.50545 11.4185 8.03144 12.0034 6.50033 11.9994H6.41783C5.43517 11.986 4.47085 11.7313 3.60964 11.2579C2.74843 10.7845 2.01669 10.1068 1.47874 9.28435C0.94079 8.4619 0.613094 7.51991 0.524456 6.54116C0.435818 5.56241 0.588952 4.57687 0.970399 3.67116C1.35185 2.76545 1.94993 1.96731 2.71207 1.34687C3.47422 0.726437 4.3771 0.30271 5.34136 0.112934C6.30562 -0.0768429 7.30173 -0.0268592 8.24215 0.258492C9.18257 0.543843 10.0385 1.05582 10.7347 1.74944L11.4178 2.37444L12.6466 1.14319C12.7167 1.07303 12.806 1.02529 12.9033 1.00604C13.0006 0.986798 13.1014 0.996911 13.1929 1.0351C13.2844 1.07328 13.3625 1.13782 13.4172 1.2205C13.472 1.30318 13.5009 1.40028 13.5003 1.49944Z"
                    fill="#020618"
                  />
                </svg>
              </div>
            </button>
            <button
              className="card-btn2"
              onClick={handleStartReflection}
              disabled={!isAnswerSelected || isSaving}
            >
              <span className="btn2-text">
                {isSaving
                  ? "Saving..."
                  : questionIndex === questions.length - 1
                  ? (activeTab || domain || "").toLowerCase().replace(/[\s-]+/g, "_") === "intellectual"
                    ? "Complete & View Report"
                    : "Complete Section"
                  : "Reflect Further"}
              </span>
              {!isSaving && <img className="arrow" src={ArrowCircleRight} alt="Arrow" />}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
